import {
  Box,
  Button,
  ClickAwayListener,
  Menu,
  MenuItem,
  Popper,
  Skeleton,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import {
  ConversationChannelTypeExtended,
  ConversationMessageType,
} from '@provision/chat-utils';
import { ConversationMessageDispatch } from '@provision/entity-types';
import { RichTextEditorFile } from '@provision/rich-text-editor';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MessageBox, MessageType } from 'react-chat-elements';
import { isNullish } from 'remeda';
import { PartnerTimeZoneTooltip } from 'src/components';
import {
  ReactionsFooter,
  TEAM_ONLY_REACTIONS_NOTICE,
  useReactionPicker,
} from 'src/components/Reactions';
import { REPLY_ROW_CLASS } from 'src/components/Replies';
import ShadowRoot from 'src/components/ShadowRoot';
import { useTextWithUI } from 'src/components/TextWithUI/use-text-with-ui';
import { ConversationParticipant, useGroupRules, UserGroupRules } from 'src/graphql';
import { isTempMessageId } from 'src/graphql/entities/conversation/temp-message-id';
import { useMessageReactions } from 'src/graphql/entities/conversation-message-reaction';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import {
  CONVERSATION_CARD_BORDER_RADIUS,
  CONVERSATION_CARD_PADDING_X,
  CONVERSATION_CARD_PADDING_Y,
  CONVERSATION_ROW_GUTTER,
  MAX_CONVERSATION_MESSAGE_HEIGHT_REM,
} from 'src/pages/Conversations/constants';
import { useDisplayTime } from 'src/utils/hooks';
import { convertNumberToRemNumber } from 'src/utils/html';
import { ChatMessageEditMode } from './chat-message-edit-mode';
import { DISPATCH_STATUS_ICON_SIZE } from './dispatch-status/dispatch-status-icon';
import { DispatchStatusTooltip } from './dispatch-status/dispatch-status-tooltip';
import { hasFailedDispatch } from './dispatch-status/dispatch-status.utils';
import { MessageAttachments } from './message-attachments';
import { MESSAGE_TOOLBAR_CLASS, MessageToolbar } from './message-toolbar';
import { MessageThread } from './MessageThread/message-thread';
import { RedactButton, RedactionControls } from './redaction-controls';
import { useMessageContextMenu } from './use-message-context-menu';
import { isEmailMessageShouldBeDisplayedUnstyled, isFullDocument } from './utils';

interface MessageProps {
  id: string;
  text: string;
  type: MessageType['type'];
  createdAt: string;
  messageType: ConversationMessageType;
  partnerId?: number | null;
  emailFileKey?: string;
  attachments?: RichTextEditorFile[];
  onUpdate?: (message: {
    conversationMessageId: string;
    body: string;
    editedAt?: string;
  }) => void;
  onEdit?: (messageId: string, newBody: string, newAttachments: RichTextEditorFile[]) => void;
  readonly?: boolean;
  isEditable?: boolean;
  channel: ConversationChannelTypeExtended;
  reactionsEnabled?: boolean;
  repliesEnabled?: boolean;
  sender?: (ConversationParticipant & { fullName?: string }) | null;
  messageDispatches?: ConversationMessageDispatch[];
  receiver?: ParticipantSelectorItem;
  metaData?: Record<string, Record<string, unknown[]>>;
}

const MessageShowMore = styled(Button, {
  name: 'MessageShowMore',
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{
  isCollapsed: boolean;
}>(({ theme, isCollapsed }) => {
  return {
    marginTop: !isCollapsed ? '0.6rem' : 'unset',
    alignSelf: 'center',
    background: theme.palette.custom.OV.sys.surfaceContainerHighest,
    border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    borderRadius: '999px',
    color: theme.palette.custom.OV.sys.onSurface,
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.71875rem',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'auto',
    cursor: 'pointer',
    padding: '0.1875rem 0.75rem',

    '&:hover': {
      background: theme.palette.custom.OV.sys.surfaceContainerHighest,
      color: theme.palette.custom.OV.sys.onBackground,
      borderColor: theme.palette.custom.OV.sys.outline,
    },
  };
});

export const MessageTimestamp = styled(Typography, {
  name: 'MessageTimestamp',
})(({ theme }) => ({
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  fontSize: '0.6rem',
  whiteSpace: 'nowrap',
  /*
   * Taken out of flow: the rendered time string is wider than the gutter, so as a flex item it
   * pushed the bubble past the gutter other row types align to.
   */
  position: 'absolute',
  left: 0,
  top: 0,
  width: `calc(${CONVERSATION_ROW_GUTTER} - ${theme.spacing(0.5)})`,
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  marginLeft: 0,
  marginRight: 0,
  paddingRight: theme.spacing(0.75),
  textAlign: 'right',
}));

const MessageContainer = styled(Stack, {
  name: 'MessageContainer',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '94%',
  justifyContent: 'flex-start',
  alignItems: 'start',
  position: 'relative',
  paddingLeft: `calc(${CONVERSATION_ROW_GUTTER} - ${theme.spacing(0.5)})`,

  '&:hover .timestamp': {
    opacity: 1,
  },
}));

const StyledMessage = styled(MessageBox, {
  name: 'StyledMessage',
  shouldForwardProp: (prop) =>
    !['isCollapsible', 'backgroundColor', 'isCollapsed', 'unstyled'].includes(prop as string),
})<{
  backgroundColor: string;
  isCollapsible: boolean;
  isCollapsed: boolean;
  unstyled: boolean;
}>(({ theme, backgroundColor, isCollapsible, isCollapsed, unstyled }) => {
  const messageStyles = {
    backgroundColor,
    alignSelf: 'flex-start',
  };

  return {
    minWidth: theme.utils.important('auto'),
    maxHeight:
      isCollapsible && isCollapsed ? `${MAX_CONVERSATION_MESSAGE_HEIGHT_REM}rem` : 'unset',
    transition: 'max-height 0.4s',
    '& .rce-mbox': {
      flexShrink: 0,
      padding: 0,
      margin: theme.utils.important('0'),
      minWidth: '1rem',
      boxShadow: 'none',
      ...(!unstyled ? { whiteSpace: 'pre-wrap' } : {}),
      ...messageStyles,

      /* This preserves proper whitespace between paragraphs in messages. */
      '& .rce-mbox-text > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      },

      /* The library ships 13.6px; the composer is set to the same body2 size so a message reads as it was typed. */
      '& .rce-mbox-text': {
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: 1.5,
      },

      '& .rce-mbox-text::after': {
        content: 'none',
      },
    },

    '&.rce-container-mbox': {
      minWidth: '100px',
      justifyContent: isCollapsible ? 'flex-start' : 'flex-end',
      display: 'flex',
      width: '100%',
      maskImage:
        isCollapsible && isCollapsed
          ? 'linear-gradient(to bottom, #000 50%, transparent 100%)'
          : 'unset',
      overflowX: 'auto',
      overflowY: 'hidden',
    },

    /*
     * Message content styling.
     */
    ...(!unstyled
      ? {
          '& p, & pre': {
            margin: theme.spacing(0),
            whiteSpace: 'pre-wrap',
            minHeight: '1rem',

            '&:only-child': {
              margin: 0,
            },
          },
          '& img': {
            maxWidth: '100%',
            cursor: 'pointer',
            borderRadius: theme.shape.borderRadius,
            objectFit: 'scale-down',
            height: 'auto',
          },

          '& pre': {
            margin: theme.spacing(0, 0.25),
          },
          '& code': {
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
            borderRadius: '0.25rem',
            boxDecorationBreak: 'clone',
            color: theme.palette.custom.OV.sys.onSurface,
            fontSize: '0.9rem',
            padding: '0.25rem',
          },
        }
      : {}),
    '& .file-message-preview-file-name': {
      color: theme.palette.custom.OV.sys.onSurface,
    },
    '& .file-message-preview-file-size': {
      color: theme.palette.custom.OV.sys.onSurfaceVariant,
    },
  };
});

/*
 * Cards hold a width instead of hugging their text, so a one-line message reads as the same card
 * as a long one. The same widths apply at every breakpoint.
 */
const cardWidth = (width: string) => ({ width, maxWidth: width });

const CARD_WIDTH = '90%';
/* Spacing units between a reply and its dispatch status icon. */
const DISPATCH_STATUS_GAP = 1;

const StyledMessageContainer = styled(Stack, {
  name: 'StyledMessageContainer',
  shouldForwardProp: (prop) =>
    !['backgroundColor', 'isPrivate', 'isFailed'].includes(prop as string),
})<{
  backgroundColor: string;
  isPrivate: boolean;
  isFailed: boolean;
}>(({ theme, isPrivate, backgroundColor, isFailed }) => {
  return {
    position: 'relative',
    backgroundColor,
    borderRadius: CONVERSATION_CARD_BORDER_RADIUS,
    border: isPrivate
      ? `1px solid ${theme.palette.custom.OV.custom.InternalNotes.noteMessageBorder}`
      : 'none',
    ...(isFailed
      ? {
          borderLeft: `3px solid ${theme.palette.error.main}`,
          marginBottom: theme.spacing(2.5),
        }
      : {}),
    padding: theme.spacing(CONVERSATION_CARD_PADDING_Y, CONVERSATION_CARD_PADDING_X),
    /* A note ends where a reply's dispatch status icon ends, so their right edges line up. */
    ...cardWidth(
      isPrivate
        ? `calc(${CARD_WIDTH} + ${theme.spacing(DISPATCH_STATUS_GAP)} + ${DISPATCH_STATUS_ICON_SIZE})`
        : CARD_WIDTH,
    ),

    /*
     * The card, not the row: the row spans the thread width, so hovering far from the bubble kept the toolbar up.
     * Only the card's own toolbar, and not while a reply inside the card is hovered: replies have their own.
     */
    [`&:hover:not(:has(.${REPLY_ROW_CLASS}:hover)) > .${MESSAGE_TOOLBAR_CLASS}`]: {
      opacity: 1,
    },
  };
});

/* Bleeds past the card's padding, so the thread's rows, lines and hover tint run edge to edge. */
const MessageThreadContainer = styled(Box, { name: 'MessageThreadContainer' })(
  ({ theme }) => ({
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-CONVERSATION_CARD_PADDING_X),
    marginRight: theme.spacing(-CONVERSATION_CARD_PADDING_X),
    marginBottom: theme.spacing(-CONVERSATION_CARD_PADDING_Y),
    overflow: 'hidden',
    borderBottomLeftRadius: `calc(${CONVERSATION_CARD_BORDER_RADIUS} - 1px)`,
    borderBottomRightRadius: `calc(${CONVERSATION_CARD_BORDER_RADIUS} - 1px)`,
    '&:empty': {
      display: 'none',
    },
  }),
);

export const Message = memo(
  ({
    id,
    text,
    type,
    createdAt,
    partnerId,
    attachments,
    messageType,
    emailFileKey,
    onUpdate,
    onEdit,
    readonly,
    channel,
    messageDispatches,
    sender,
    reactionsEnabled = true,
    repliesEnabled = true,
    receiver,
    isEditable,
    metaData,
  }: MessageProps) => {
    const { displayTime } = useDisplayTime();

    const { checkValidity } = useGroupRules();

    const hasAccessToEditInternalNotes = checkValidity([
      UserGroupRules.AccessToEditInternalNotes,
    ]);

    const [isEditing, setIsEditing] = useState(false);

    const timestamp = useMemo(
      () => (
        <MessageTimestamp className="timestamp">
          <PartnerTimeZoneTooltip date={createdAt} partnerId={partnerId}>
            {displayTime(createdAt)}
          </PartnerTimeZoneTooltip>
        </MessageTimestamp>
      ),
      [createdAt, displayTime, partnerId],
    );

    const theme = useTheme();

    const containerRef = useRef<HTMLDivElement>(null);
    // The reactions row sits in the card but must not count toward the collapse threshold.
    const reactionsRef = useRef<HTMLDivElement>(null);
    const threadRef = useRef<HTMLDivElement>(null);

    const [isCollapsible, setIsCollapsible] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);

    /*
      Runs synchronously before paint, so plain-text messages (already final height
      at layout time) get their collapsed cap + Show More on the very first frame —
      no full-height flash on mount, including the double-mount from the optimistic
      message's temp id being swapped for the server id.
    */
    useLayoutEffect(() => {
      if (!containerRef.current) {
        return;
      }

      const elementHeight =
        containerRef.current.offsetHeight -
        (reactionsRef.current?.offsetHeight ?? 0) -
        (threadRef.current?.offsetHeight ?? 0);
      const elemHightInRem = convertNumberToRemNumber(elementHeight);
      setIsCollapsible(elemHightInRem > MAX_CONVERSATION_MESSAGE_HEIGHT_REM);
    }, [containerRef]);

    useEffect(() => {
      if (!containerRef.current) {
        return;
      }

      // micro delay to let nested shadow dom render by the time we measure the height
      setTimeout(() => {
        const elementHeight =
          (containerRef.current?.offsetHeight ?? 0) -
          (reactionsRef.current?.offsetHeight ?? 0) -
          (threadRef.current?.offsetHeight ?? 0);
        const elemHightInRem = convertNumberToRemNumber(elementHeight);
        setIsCollapsible(elemHightInRem > MAX_CONVERSATION_MESSAGE_HEIGHT_REM);
      }, 100);
    }, [containerRef]);

    const handleCollapse = useCallback(() => {
      setIsCollapsed((prev) => !prev);
    }, []);

    const backgroundColor = useMemo(() => {
      if (messageType === ConversationMessageType.Private) {
        return theme.palette.custom.OV.custom.InternalNotes.noteMessageContainer;
      }

      if (messageType === ConversationMessageType.Engage) {
        return theme.palette.custom.OV.sys.surfaceContainerHighest;
      }

      return theme.palette.custom.OV.sys.tertiaryContainer;
    }, [messageType, theme]);

    const isPrivate = useMemo(
      () => messageType === ConversationMessageType.Private,
      [messageType],
    );

    const { reactions, isReactable, addReaction, toggleReaction } = useMessageReactions(id);
    /* Reactions never reach the client, which a reaction on a client-visible message could suggest. */
    const reactionsNotice = isPrivate ? undefined : TEAM_ONLY_REACTIONS_NOTICE;

    const unstyled = useMemo(() => {
      if (isPrivate || channel !== ConversationChannelTypeExtended.Email) {
        return false;
      }

      return isEmailMessageShouldBeDisplayedUnstyled(text);
    }, [isPrivate, channel, text]);

    const {
      open,
      isRedacting,
      handleMenuClick,
      handleMenuClose,
      menuAnchorEl,
      selectionCoordinates,
      setSelectionCoordinates,
      handleRedactClick,
      handleOpenOriginalEmailClick,
      handleRedactSelection,
      handleCancelRedaction,
      handleConfirmRedaction,
      handleCopyMessageIdToClipboard,
      redactedText,
    } = useMessageContextMenu({
      id,
      message: text,
      container: containerRef.current,
      onRedactStart: useCallback(() => setIsCollapsed(false), []),
      onConfirm: useCallback(
        (message) => {
          onUpdate?.({ conversationMessageId: id, body: message });
        },
        [onUpdate, id],
      ),
    });

    /* A long press while redacting is a text selection, not a request for the picker. */
    const canReact =
      reactionsEnabled && isReactable && !readonly && !isEditing && !isRedacting;
    /*
     * The reactions row and the reply thread handle their own presses: a long press on a chip
     * shows who reacted, and one on a reply reacts to that reply.
     */
    const reactionPicker = useReactionPicker({
      canReact,
      anchorRef: containerRef,
      ignoredRefs: [reactionsRef, threadRef],
    });

    const {
      text: textWithUI,
      handlers: textWithUIHandlers,
      parentStyles: textWithUIParentStyles,
    } = useTextWithUI(redactedText, metaData);

    /*
     * React 19 compares the `dangerouslySetInnerHTML` object by identity and re-sets innerHTML on
     * every mismatch, which rebuilds the text nodes and drops the selection being redacted.
     */
    const messageHtml = useMemo(() => ({ __html: textWithUI }), [textWithUI]);

    const selectionAnchor = useMemo(
      () =>
        selectionCoordinates && {
          getBoundingClientRect: () =>
            new DOMRect(selectionCoordinates.left, selectionCoordinates.top, 0, 0),
        },
      [selectionCoordinates],
    );

    const hideRedactSelectionButton = useCallback(
      (event: MouseEvent | TouchEvent) => {
        /* Pointer-ups inside the card are the hook's job: they either move the button to the new selection or clear it. */
        if (event.target instanceof Node && containerRef.current?.contains(event.target)) {
          return;
        }
        setSelectionCoordinates(null);
      },
      [setSelectionCoordinates],
    );

    const requiresDomScoping = useMemo(() => {
      if (channel !== ConversationChannelTypeExtended.Email) {
        return false;
      }

      return isFullDocument(text);
    }, [channel, text]);

    const messageMenuItems = useMemo(() => {
      const menuItems = [];
      if (isEditable && !readonly && hasAccessToEditInternalNotes) {
        menuItems.push({
          label: 'Edit message',
          action: () => setIsEditing(true),
        });
      }
      /* Notes are where sensitive client details get pasted, so they are redactable like dispatched messages. */
      if (!readonly && (messageDispatches?.length || isPrivate)) {
        menuItems.push({
          label: 'Redact message',
          action: handleRedactClick,
        });
      }
      if (messageDispatches?.length && emailFileKey) {
        menuItems.push({
          label: 'View original email',
          action: handleOpenOriginalEmailClick,
        });
      }

      /* Every message has an id worth copying, including notes that were never dispatched. */
      menuItems.push({
        label: 'Copy message ID',
        action: handleCopyMessageIdToClipboard,
      });

      return menuItems;
    }, [
      emailFileKey,
      handleCopyMessageIdToClipboard,
      handleOpenOriginalEmailClick,
      handleRedactClick,
      hasAccessToEditInternalNotes,
      isEditable,
      isPrivate,
      messageDispatches?.length,
      readonly,
    ]);

    const handleCancelEdit = useCallback(() => {
      setIsEditing(false);
    }, []);

    const handleSaveEdit = useCallback(
      (newBody: string, newAttachments: RichTextEditorFile[]) => {
        if (newBody !== text || newAttachments !== attachments) {
          onEdit?.(id, newBody, newAttachments);
        }
        setIsEditing(false);
      },
      [text, attachments, onEdit, id],
    );

    const isFailedOutboundMessage =
      messageType === ConversationMessageType.Assist &&
      hasFailedDispatch(messageDispatches ?? []);

    const shouldShowMore = messageMenuItems.length > 0 && !isEditing && !isRedacting;
    const hasDispatchStatus =
      !!messageDispatches?.length && messageType === ConversationMessageType.Assist;
    return (
      <MessageContainer data-testid="chat-message" data-message-id={id}>
        {timestamp}
        <StyledMessageContainer
          isPrivate={isPrivate}
          isFailed={isFailedOutboundMessage}
          backgroundColor={backgroundColor}
          ref={containerRef}
          {...reactionPicker.longPressHandlers()}
        >
          {!isEditing && !isRedacting && (
            <MessageToolbar
              messageHtml={textWithUI}
              showMenu={shouldShowMore}
              isMenuOpen={open}
              onMenuClick={handleMenuClick}
              onAddReactionClick={canReact ? reactionPicker.handleAddReactionClick : undefined}
              isReactionPickerOpen={reactionPicker.isOpen}
            />
          )}

          {!!shouldShowMore && (
            <V2ThemeProvider>
              <Menu anchorEl={menuAnchorEl} open={open} onClose={handleMenuClose}>
                {messageMenuItems.map((item) => (
                  <MenuItem
                    key={item.label}
                    onClick={() => {
                      item.action();
                      handleMenuClose();
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </V2ThemeProvider>
          )}
          {isEditing ? (
            <ChatMessageEditMode
              onClose={handleCancelEdit}
              onSave={handleSaveEdit}
              content={text}
              attachments={attachments}
              partnerId={partnerId ?? null}
            />
          ) : (
            <>
              {' '}
              <StyledMessage
                id={id}
                // @ts-expect-error Incorrect component typings
                text={
                  requiresDomScoping ? (
                    <ShadowRoot>
                      <div dangerouslySetInnerHTML={messageHtml} />
                    </ShadowRoot>
                  ) : (
                    <div dangerouslySetInnerHTML={messageHtml} />
                  )
                }
                type={type}
                backgroundColor={backgroundColor}
                notch={false}
                isCollapsible={isCollapsible}
                isCollapsed={isCollapsed}
                unstyled={unstyled}
                sx={textWithUIParentStyles}
                {...textWithUIHandlers}
              />
              {isRedacting ? (
                <RedactionControls mt={1} borderTop={1}>
                  <Button
                    size="small"
                    sx={{ marginLeft: 'auto' }}
                    color="inherit"
                    onClick={handleCancelRedaction}
                  >
                    Cancel
                  </Button>
                  <Button size="small" color="primary" onClick={handleConfirmRedaction}>
                    Redact
                  </Button>
                </RedactionControls>
              ) : null}
              {!!attachments?.length && <MessageAttachments attachments={attachments} />}
              {isCollapsible ? (
                <MessageShowMore onClick={handleCollapse} isCollapsed={isCollapsed}>
                  {!isCollapsed ? 'Show Less' : 'Show More'}
                </MessageShowMore>
              ) : null}
              {reactionsEnabled ? (
                <ReactionsFooter
                  reactionsRef={reactionsRef}
                  picker={reactionPicker}
                  reactions={reactions}
                  canReact={canReact}
                  onToggle={toggleReaction}
                  onSelect={addReaction}
                  notice={reactionsNotice}
                />
              ) : null}
              {repliesEnabled && isPrivate && !isNullish(partnerId) ? (
                <MessageThreadContainer ref={threadRef}>
                  <MessageThread
                    conversationMessageId={id}
                    partnerId={partnerId}
                    // A note without a server id has no row to reply to yet.
                    readonly={!!readonly || isTempMessageId(id)}
                  />
                </MessageThreadContainer>
              ) : null}
              {isFailedOutboundMessage ? (
                <Typography
                  variant="caption"
                  color="error.main"
                  fontSize="0.6875rem"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: (theme) => theme.spacing(1),
                    mt: 0.25,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Not delivered
                </Typography>
              ) : null}
              {/*
               * A non-modal Popper: Popover's backdrop swallowed the pointer-down of the next selection
               * and its focus trap collapsed the current one.
               */}
              <Popper
                open={Boolean(selectionAnchor)}
                // we are using shadow dome to scope messages that are raw HTML elements
                // therefore it is not possible to use redaction text anchors
                anchorEl={selectionAnchor}
                placement="top"
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                sx={{ zIndex: theme.zIndex.tooltip }}
              >
                <ClickAwayListener onClickAway={hideRedactSelectionButton}>
                  <RedactButton onClick={handleRedactSelection}>Mark as redacted</RedactButton>
                </ClickAwayListener>
              </Popper>
            </>
          )}
        </StyledMessageContainer>
        {!!hasDispatchStatus && (
          <Box ml={DISPATCH_STATUS_GAP} mt="0.4375rem" flexShrink={0}>
            <DispatchStatusTooltip
              from={{
                fullName: sender?.fullName || '',
                email:
                  messageDispatches?.[0]?.participation?.channel?.internalIdentifier ?? '',
              }}
              to={receiver}
              dispatches={messageDispatches}
            />
          </Box>
        )}
      </MessageContainer>
    );
  },
);

Message.displayName = 'Message';

export const MessageItemSkeleton = () => (
  <Stack direction="column" alignItems="flex-start" gap={0.5} p={0.5} width="100%">
    <Stack direction="row" alignItems="center" gap={0.5} overflow="hidden">
      <Skeleton variant="circular" width="2rem" height="2rem" sx={{ mx: 0.25 }} />
      <Skeleton width="10rem" height="1.4rem" />
    </Stack>
    {/* Indent matches the timestamp gutter that offsets a real bubble from the group edge. */}
    <Stack width="100%" pl="2.875rem" gap={0.5}>
      <Skeleton
        variant="rounded"
        width="55%"
        height="3.25rem"
        sx={{ borderRadius: CONVERSATION_CARD_BORDER_RADIUS }}
      />
      <Skeleton
        variant="rounded"
        width="30%"
        height="2rem"
        sx={{ borderRadius: CONVERSATION_CARD_BORDER_RADIUS }}
      />
    </Stack>
  </Stack>
);
